'use client';
import { CatCard, CatCheckboxField } from 'components/blocks-components';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import Image from 'next/image';
import styles from './fbt-content-item.module.scss';
import Link from 'next/link';
import FBTPrice from './fbt-content-price';
import FbtFitment from '../../fbt-fitment';
import { FC } from 'react';
import { CurrentItems } from 'services/fbtItems/types';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { useFbtMobileDrawer } from '../fbt-mobile-drawer-context';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import { FBT_LIST_NAME } from '../../../../constants/constants';

interface FbtItemCardProps {
  fbtItem: CurrentItems;
  partsPriceData: ProductDealerPriceAvailability[];
  currency: string;
  isCatCorp: boolean;
  frequestBoughtMobile: boolean;
  isCurrentItem?: boolean;
  contactDealerComp?: React.ReactNode;
  handleCheckboxChange?: (
    e: CustomEvent<{ checked: boolean; indeterminate: boolean; value: string }>
  ) => void;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  cartId: string;
  storeLocationId: string;
  startIndex: number;
}

const FbtItemCard: FC<FbtItemCardProps> = ({
  fbtItem,
  partsPriceData,
  currency,
  isCatCorp,
  frequestBoughtMobile,
  startIndex,
  isCurrentItem = false,
  cartId,
  contactDealerComp,
  handleCheckboxChange,
  storeLocationId,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const { closeDrawer } = useFbtMobileDrawer();
  const {
    imageURL = '',
    partNumber = '',
    name = '',
    isSerialCompatible = false
  } = fbtItem || {};

  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const getPartPrice = (partNumber: string) => {
    const pricedata = partsPriceData?.filter((item) => {
      return partNumber === item.partNumber;
    });
    return pricedata?.length > 0 && pricedata[0];
  };

  const partPrice = getPartPrice(partNumber);
  const isCheckboxDisabled = !partPrice?.enableAddToCart;
  const isPartsPrice =
    partsPriceData?.filter((item) => item?.isChecked).length === 0;

  const handleClick = (e) => {
    if (isCurrentItem) {
      e.preventDefault();
      closeDrawer();
    }
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem({ orderItemId: cartId }, partPrice),
      partName: `${partPrice?.partNumber}: ${name}`,
      locationId: storeLocationId,
      itemSummary: {
        categoryPath: partPrice?.categoryPath,
        manufacturer: fbtItem?.manufacturer ?? 'Cat'
      },
      isSearchPageEvent: true,
      itemListId: FBT_LIST_NAME,
      itemListName: FBT_LIST_NAME,
      serialNumber: '',
      fitment: '',
      index: startIndex
    });
  };

  return (
    <CatCard>
      <div
        className={`${styles['fbt-content__fbt-item']} d-flex align-items-center `}
      >
        {!isCatCorp ? (
          <CatCheckboxField
            checked={partPrice?.isChecked}
            disabled={isCheckboxDisabled}
            id={partNumber}
            onBlCheckboxChange={handleCheckboxChange}
            data-testid={`${partNumber}-checkbox`}
            className={styles['checkbox']}
          />
        ) : (
          <></>
        )}

        <Image
          src={imageURL || PRODUCT_FALLBACK_IMAGE}
          alt={partNumber}
          loading="lazy"
          width={80}
          height={80}
          unoptimized
          className={styles['product-image']}
        />

        <div className="m-2">
          <Link
            href={partNumber}
            className={`text-decoration-underline ${styles['fbt-content__title']}`}
            onClick={handleClick}
          >
            {`${partNumber}: ${name}`}
          </Link>

          <FBTPrice
            partNumber={partNumber}
            currency={currency}
            partsPriceData={partsPriceData}
            isCatCorp={isCatCorp}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
          />
        </div>
      </div>
      <div className={styles['fbt-content__fbt-item-container']}>
        <FbtFitment isSerialCompatible={isSerialCompatible} />
        <div className={styles['fbt-content__contact-dealer']}>
          {!isPartsPrice &&
            isCheckboxDisabled &&
            !isCatCorp &&
            contactDealerComp}
        </div>
      </div>
    </CatCard>
  );
};

export default FbtItemCard;
